import React, { useEffect, useState } from 'react';
import { statsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, BarChart3, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const fmt = (v) => v === null || v === undefined ? '—' : Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 });

export default function SectorStats() {
  const [data, setData] = useState(null);
  useEffect(() => { statsApi.sector().then(r => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-aea-blue" /></div>;

  const chartInds = (data.indicators || []).filter(i => !i.insufficient_data);
  const grouped = chartInds.reduce((acc, i) => { (acc[i.category] = acc[i.category] || []).push(i); return acc; }, {});

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Card className="aea-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-aea-blue" /> Estadísticas sectoriales · {data.period_label || '—'}</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Estadísticas agregadas del sector. Los indicadores con menos de 3 empresas se muestran como no disponibles (regla de anonimato).</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3">Indicador</th>
                  <th className="text-center p-3">n</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-right p-3">Media</th>
                  <th className="text-right p-3">Mediana</th>
                  <th className="text-right p-3">P25</th>
                  <th className="text-right p-3">P75</th>
                  <th className="text-right p-3">Min / Max</th>
                </tr>
              </thead>
              <tbody>
                {(data.indicators || []).map((i) => (
                  <tr key={i.indicator_code} className="border-t" data-testid={`sector-row-${i.indicator_code}`}>
                    <td className="p-3">
                      <div className="font-medium">{i.indicator_name}</div>
                      <div className="text-xs text-slate-400 font-mono">{i.indicator_code} · {i.unit}</div>
                    </td>
                    <td className="p-3 text-center font-mono text-xs">{i.n}</td>
                    {i.insufficient_data ? (
                      <td className="p-3 text-center text-xs text-amber-700 bg-amber-50" colSpan={6}>
                        <AlertTriangle className="inline w-3.5 h-3.5 mr-1" /> Datos insuficientes (mín. 3 empresas)
                      </td>
                    ) : (
                      <>
                        <td className="p-3 text-right font-mono">{fmt(i.total)}</td>
                        <td className="p-3 text-right font-mono font-semibold">{fmt(i.mean)}</td>
                        <td className="p-3 text-right font-mono">{fmt(i.median)}</td>
                        <td className="p-3 text-right font-mono text-xs">{fmt(i.p25)}</td>
                        <td className="p-3 text-right font-mono text-xs">{fmt(i.p75)}</td>
                        <td className="p-3 text-right font-mono text-xs text-slate-500">{fmt(i.min)} / {fmt(i.max)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Gráficos por categoría */}
      {Object.entries(grouped).map(([cat, list]) => (
        <Card key={cat} className="aea-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-widest text-aea-blue">{cat}</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={list.map(i => ({ name: i.indicator_name.slice(0, 20), Media: Math.round(i.mean), Mediana: Math.round(i.median) }))} margin={{ top: 8, right: 8, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-10} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                  <Bar dataKey="Media" fill="#0369A1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Mediana" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
